import { Component, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { of, switchMap, combineLatest } from 'rxjs';

import { AsideService } from '../../../core/services/aside.service';
import { LoadService } from '../../../core/services/load.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { ModalService } from '../../../core/services/modal.service';
import { LoadWithView } from '../../../core/models/load.model';
import { Stop } from '../../../core/models/stop.model';
import { computeLoadLineItem } from '../../../shared/utilities/invoice-pricing';
import { validateLoadBillingDocuments } from '../../../shared/utilities/billing-document-validation';

import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { CheckboxComponent } from '../../../shared/ui/forms/checkbox/checkbox.component';
import { HeadingComponent } from '../../../shared/ui/typography/heading/heading.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { LoadingComponent } from '../../../shared/ui/loading/loading.component';

import { EditPricingModalComponent } from '../edit-pricing-modal/edit-pricing-modal.component';
import { TonuDecisionModalComponent } from '../tonu-decision-modal/tonu-decision-modal.component';
import { MarkPaidModalComponent } from '../mark-paid-modal/mark-paid-modal.component';

@Component({
  selector: 'app-ready-load-aside',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BadgeComponent,
    ButtonComponent,
    CheckboxComponent,
    HeadingComponent,
    TextComponent,
    LucideIconComponent,
    LoadingComponent,
  ],
  templateUrl: './ready-load-aside.component.html',
  host: { class: 'flex flex-col h-full overflow-hidden' },
})
export class ReadyLoadAsideComponent {
  private readonly asideService = inject(AsideService);
  private readonly loadService = inject(LoadService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  readonly context = computed(() => {
    const ctx = this.asideService.asideContext();
    if (ctx?.type !== 'view-ready-load') return null;
    return ctx.data;
  });

  readonly loadId = computed(() => this.context()?.loadId || null);

  readonly load = toSignal<LoadWithView | null>(
    combineLatest([
      toObservable(this.authService.companyId),
      toObservable(this.loadId),
    ]).pipe(
      switchMap(([cid, id]) => {
        if (!id || !cid) return of(null);
        return this.loadService.loadByIdWithView$(id, cid);
      })
    ),
    { initialValue: null }
  );

  readonly originalBookedRate = computed(() => {
    const l = this.load();
    if (!l) return 0;
    return Number(l.view?.pricing?.baseRate ?? (l.view as any)?.bookedAtRate ?? 0);
  });

  readonly isPendingTonu = computed(() => {
    const l = this.load();
    if (!l) return false;
    const tonuStatus = l.view?.tonuDecision?.status;
    if (tonuStatus === 'pending') return true;
    return (
      l.status === 'CANCELLED' &&
      tonuStatus !== 'eligible' &&
      !(l.view?.pricing?.tonu && l.view.pricing.tonu > 0)
    );
  });

  // Line item breakdown calculation
  readonly pricingBreakdown = computed(() => {
    const l = this.load();
    if (!l) return null;
    const pricing = l.view?.pricing;
    const bookedAt = (l.view as any)?.bookedAtRate;
    const tonuDecision = l.view?.tonuDecision;
    const item = computeLoadLineItem(pricing, bookedAt, tonuDecision);
    if (this.isPendingTonu()) {
      return {
        ...item,
        baseRate: 0,
        fuelSurcharge: 0,
        total: 0,
      };
    }
    return item;
  });

  // Counterparty display
  readonly bookedFrom = computed(() => {
    const l = this.load();
    if (!l) return null;
    const view = l.view;
    const name = view?.bookedFromCompanyName || view?.customerName || view?.brokerName || 'Unknown';
    const role = view?.bookedFromRole || (view?.customerName ? 'direct_shipper' : 'broker');
    const dotNumber = view?.bookedFromDotNumber || view?.brokerDotNumber || view?.customerDotNumber;
    const mcNumber = view?.bookedFromMcNumber || view?.brokerMcNumber || view?.customerMcNumber;
    return { name, role, dotNumber, mcNumber };
  });

  // Stop dwell time & detention analysis
  readonly stopDwellDetails = computed(() => {
    const l = this.load();
    if (!l || !l.stops) return [];
    return l.stops.map((stop: Stop) => {
      let dwellMinutes: number | null = null;
      let detentionHours = 0;

      if (stop.arrivedAt && stop.departedAt) {
        const start = stop.arrivedAt.toMillis ? stop.arrivedAt.toMillis() : new Date(stop.arrivedAt as any).getTime();
        const end = stop.departedAt.toMillis ? stop.departedAt.toMillis() : new Date(stop.departedAt as any).getTime();
        dwellMinutes = Math.max(0, Math.round((end - start) / 60000));
        detentionHours = Math.max(0, (dwellMinutes - 120) / 60);
      } else if (stop.detentionHours) {
        detentionHours = stop.detentionHours;
      }

      return {
        stopNumber: stop.stopNumber,
        stopType: stop.stopType,
        facilityName: stop.companyName || stop.address?.rawText || `Stop #${stop.stopNumber}`,
        cityState: stop.address ? `${stop.address.city || ''}, ${stop.address.state || ''}` : '',
        dwellMinutes,
        detentionHours: Number(detentionHours.toFixed(2)),
        isDetentionEligible: detentionHours > 0,
      };
    });
  });

  readonly totalDetentionHours = computed(() => {
    return this.stopDwellDetails().reduce((sum, s) => sum + s.detentionHours, 0);
  });

  // Document verification checks
  readonly documentVerification = computed(() => {
    const l = this.load();
    if (!l || !l.stops) return { allApproved: true, pendingDocsCount: 0, docs: [] };

    const docs: { stopNumber: number; type: string; status: string; url?: string }[] = [];
    let pendingDocsCount = 0;

    l.stops.forEach((s: Stop) => {
      if (s.requiresApproval) {
        const isApproved = s.approvedBy != null || s.status === 'APPROVED' || s.approvalStatus === 'approved';
        if (!isApproved) pendingDocsCount++;
        docs.push({
          stopNumber: s.stopNumber ?? 0,
          type: s.stopType === 'pickup' ? 'BOL' : 'POD',
          status: s.approvalStatus || (isApproved ? 'approved' : (s.status === 'WAITING_APPROVAL' ? 'waiting_approval' : 'pending')),
        });
      }
    });

    return {
      allApproved: pendingDocsCount === 0,
      pendingDocsCount,
      docs,
    };
  });

  readonly billingDocValidation = computed(() => {
    const l = this.load();
    if (!l) {
      return {
        isComplete: true,
        missingDocs: [],
        summaryText: '',
        isLumperReceiptWaived: false,
        hasLumperCharge: false,
      };
    }
    return validateLoadBillingDocuments(l, l.view);
  });

  readonly hasMissingLumperReceipt = computed(() =>
    this.billingDocValidation().missingDocs.some(
      (d) => d.type === 'MISSING_LUMPER_RECEIPT'
    )
  );

  isSavingWaiver = signal(false);

  toggleLumperReceiptWaived(checked: boolean) {
    const l = this.load();
    const companyId = this.authService.activeCompanyId();
    if (!l || !companyId || this.isSavingWaiver()) return;

    this.isSavingWaiver.set(true);
    const updatedPricing = {
      ...(l.view?.pricing || {}),
      lumperReceiptWaived: Boolean(checked),
    };

    this.loadService
      .updateCompanyView(l.id, companyId, { pricing: updatedPricing } as any)
      .subscribe({
        next: () => {
          this.isSavingWaiver.set(false);
          this.toastService.show(
            'success',
            checked
              ? 'Lumper receipt requirement waived for this load'
              : 'Lumper receipt requirement restored'
          );
          this.context()?.onEditPricing?.({ pricing: updatedPricing });
        },
        error: (err) => {
          console.error(err);
          this.isSavingWaiver.set(false);
          this.toastService.show('error', 'Failed to update lumper receipt setting');
        },
      });
  }

  close() {
    this.asideService.close();
  }

  openEditPricing() {
    const l = this.load();
    if (!l) return;
    this.modalService.open(EditPricingModalComponent, {
      title: 'Edit Pricing & Accessorials',
      subtitle: `Load #${l.externalId || l.id.slice(0, 8)}`,
      icon: 'DollarSign',
      size: 'lg',
      data: {
        load: l,
        onSuccess: (patch?: Record<string, any>) => {
          this.context()?.onEditPricing?.(patch);
        },
      },
    });
  }

  openTonuDecision() {
    const l = this.load();
    if (!l) return;
    this.modalService.open(TonuDecisionModalComponent, {
      title: 'TONU Decision',
      subtitle: `Load #${l.externalId || l.id.slice(0, 8)}`,
      icon: 'AlertTriangle',
      size: 'md',
      data: {
        load: l,
        onSuccess: (patch?: Record<string, any>) => {
          this.context()?.onDecideTonu?.(patch);
        },
      },
    });
  }

  openMarkPaid() {
    const l = this.load();
    if (!l) return;
    this.modalService.open(MarkPaidModalComponent, {
      title: 'Mark Load as Paid',
      subtitle: `Record a direct payment for load #${l.externalId || l.id.slice(0, 8)}`,
      icon: 'BadgeDollarSign',
      size: 'sm',
      data: {
        load: l,
        onSuccess: () => {
          this.toastService.show('success', 'Load payment recorded successfully');
          this.context()?.onMarkPaid?.();
          this.close();
        },
      },
    });
  }

  markWillNotInvoice() {
    const l = this.load();
    const companyId = this.authService.activeCompanyId();
    if (!l || !companyId) return;

    this.loadService.updateCompanyView(l.id, companyId, { billingStatus: 'WILL_NOT_INVOICE', 'pricing.status': 'will_not_invoice' } as any).subscribe({
      next: () => {
        this.toastService.show('success', 'Load marked as will not invoice');
        this.context()?.onWillNotInvoice?.();
        this.close();
      },
      error: (err) => {
        console.error(err);
        this.toastService.show('error', 'Failed to update load');
      },
    });
  }

  viewFullDetails() {
    const l = this.load();
    if (l) {
      this.asideService.suspend();
      this.router.navigate(['/loads', l.id]);
    }
  }
}
